import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PipelineRoundTrip from "../components/PipelineRoundTrip";
import {
  Plus,
  Search,
  Filter,
  Upload,
  Download,
  RefreshCw,
  MoreHorizontal,
  Mail,
  Trash2,
  Eye,
  X,
  Pencil,
  Check,
  Settings2,
  GripVertical,
  Circle,
  Loader2,
} from "lucide-react";
import { API, STAGES, TIERS, SECTORS, CLIENT_STATUSES, ENGAGEMENT_TYPES, CURRENCIES, CONFIDENCE_LEVELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const STATUS_OPTIONS = CLIENT_STATUSES.map(s => s.id);

// All available columns with labels
const ALL_COLUMNS = [
  { id: "health_score", label: "Health", width: "w-16" },
  { id: "company_name", label: "Company", width: "w-48" },
  { id: "engagement_type", label: "Engagement", width: "w-36" },
  { id: "tier", label: "Tier", width: "w-24" },
  { id: "sector", label: "Sector", width: "w-32" },
  { id: "country", label: "Country", width: "w-28" },
  { id: "city", label: "City", width: "w-28" },
  { id: "stage", label: "Stage", width: "w-36" },
  { id: "status", label: "Status", width: "w-28" },
  { id: "confidence_level", label: "Confidence", width: "w-28" },
  { id: "opportunity_value", label: "Opp. Value", width: "w-32" },
  { id: "expected_close_date", label: "Expected Close", width: "w-32" },
  { id: "currency", label: "Currency", width: "w-24" },
  { id: "account_manager_name", label: "Account Manager", width: "w-32" },
  { id: "deal_owner_name", label: "Deal Owner", width: "w-32" },
  { id: "last_contacted_date", label: "Last Contact", width: "w-32" },
  { id: "next_action_date", label: "Next Action", width: "w-32" },
  { id: "next_step", label: "Next Step", width: "w-40" },
  { id: "potential_offering", label: "Offering", width: "w-36" },
  { id: "tracking_notes", label: "Notes", width: "w-48" },
];

const DEFAULT_COLUMNS = ["health_score", "company_name", "stage", "engagement_type", "opportunity_value", "confidence_level", "account_manager_name", "deal_owner_name", "last_contacted_date"];

// Traffic Light Component
const TrafficLight = ({ healthScore }) => {
  if (!healthScore) return <Circle className="w-4 h-4 text-slate-300" />;
  
  const colors = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  };
  
  const tooltipText = healthScore.reasons?.join(", ") || "";
  
  return (
    <div className="flex items-center gap-1" title={tooltipText}>
      <div className={`w-3 h-3 rounded-full ${colors[healthScore.score] || "bg-slate-300"}`} />
    </div>
  );
};

// Inline Editable Cell — click to edit, blur/enter to save
const InlineEdit = ({ value, onSave, type = "text", placeholder = "-", className = "" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const save = () => {
    setEditing(false);
    const newVal = type === "number" ? (draft ? parseFloat(draft) : null) : (draft || null);
    if (newVal !== value) onSave(newVal);
  };

  if (!editing) {
    let display = value;
    if (type === "number" && value) {
      display = new Intl.NumberFormat("en-NG", { maximumFractionDigits: 0 }).format(value);
    }
    if (type === "date" && value) {
      display = new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    }
    return (
      <span
        onClick={() => setEditing(true)}
        className={`cursor-pointer hover:bg-blue-50 hover:text-blue-700 rounded px-1 py-0.5 transition-colors ${display ? "text-slate-600" : "text-slate-400 text-xs"} ${className}`}
        title="Click to edit"
      >
        {display || placeholder}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      type={type === "date" ? "date" : type === "number" ? "number" : "text"}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
      className="h-7 w-full border border-blue-300 rounded px-1 text-xs bg-white focus:ring-1 focus:ring-blue-400 outline-none"
    />
  );
};


export default function Clients({ user }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const urlParams = new URLSearchParams(window.location.search);
  const [searchQuery, setSearchQuery] = useState(urlParams.get("q") || "");
  const [filterStage, setFilterStage] = useState(urlParams.get("stage") || "all");
  const [filterTier, setFilterTier] = useState(urlParams.get("tier") || "all");
  const [filterSector, setFilterSector] = useState(urlParams.get("sector") || "all");
  const [filterStatus, setFilterStatus] = useState(urlParams.get("status") || "all");
  const [filterCountry, setFilterCountry] = useState(urlParams.get("country") || "all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Users list for ownership assignment
  const [users, setUsers] = useState([]);
  
  // Check if user can edit (admin or user role, not viewer)
  const canEdit = user?.role === "admin" || user?.role === "user";
  const isAdmin = user?.role === "admin";
  
  // Column preferences
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMNS);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [newClient, setNewClient] = useState({
    company_name: "",
    tier: "",
    sector: "",
    city: "",
    stage: "Unqualified Leads",
    engagement_type: "Commercial Opportunity",
    opportunity_value: "",
    opportunity_currency: "NGN",
    expected_close_date: "",
    contacts: [{ name: "", email: "", phone: "", title: "" }],
    tracking_notes: "",
  });
  const fileInputRef = useRef(null);
  const fileReplaceInputRef = useRef(null);

  // Extract unique values from client data for dynamic filters
  const uniqueSectors = [...new Set(clients.map(c => c.sector).filter(Boolean))].sort();
  const uniqueCountries = [...new Set(clients.map(c => c.country).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(clients.map(c => c.status).filter(Boolean))].sort();

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await fetch(`${API}/user/preferences`, { credentials: "include" });
        if (response.ok) {
          const prefs = await response.json();
          if (prefs.visible_columns?.length > 0) {
            setVisibleColumns(prefs.visible_columns);
            setColumnOrder(prefs.column_order || prefs.visible_columns);
          }
        }
      } catch (error) {
        console.error("Error loading preferences:", error);
      } finally {
        setPrefsLoaded(true);
      }
    };
    loadPreferences();
    fetchClients();
    fetchUsers();
  }, []);

  // Sync filters to URL params so back button preserves state
  useEffect(() => {
    const params = new URLSearchParams();
    if (filterStage !== "all") params.set("stage", filterStage);
    if (filterTier !== "all") params.set("tier", filterTier);
    if (filterSector !== "all") params.set("sector", filterSector);
    if (filterStatus !== "all") params.set("status", filterStatus);
    if (filterCountry !== "all") params.set("country", filterCountry);
    if (searchQuery) params.set("q", searchQuery);
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, "", newUrl);
  }, [filterStage, filterTier, filterSector, filterStatus, filterCountry, searchQuery]);


  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Save preferences when they change
  const savePreferences = async (newVisible, newOrder) => {
    try {
      await fetch(`${API}/user/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          visible_columns: newVisible,
          column_order: newOrder,
        }),
      });
    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    let newVisible;
    if (visibleColumns.includes(columnId)) {
      newVisible = visibleColumns.filter(c => c !== columnId);
    } else {
      newVisible = [...visibleColumns, columnId];
    }
    setVisibleColumns(newVisible);
    
    // Update order to include new columns at the end
    const newOrder = columnOrder.filter(c => newVisible.includes(c));
    if (!newOrder.includes(columnId) && newVisible.includes(columnId)) {
      newOrder.push(columnId);
    }
    setColumnOrder(newOrder);
    savePreferences(newVisible, newOrder);
  };

  // Handle drag start
  const handleDragStart = (e, columnId) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
  };

  // Handle drag over
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnId) {
      const newOrder = [...columnOrder];
      const dragIndex = newOrder.indexOf(draggedColumn);
      const hoverIndex = newOrder.indexOf(columnId);
      
      if (dragIndex !== -1 && hoverIndex !== -1) {
        newOrder.splice(dragIndex, 1);
        newOrder.splice(hoverIndex, 0, draggedColumn);
        setColumnOrder(newOrder);
      }
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (draggedColumn) {
      savePreferences(visibleColumns, columnOrder);
    }
    setDraggedColumn(null);
  };

  // Get ordered visible columns
  const getOrderedColumns = () => {
    return columnOrder
      .filter(id => visibleColumns.includes(id))
      .map(id => ALL_COLUMNS.find(c => c.id === id))
      .filter(Boolean);
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API}/clients`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }

    if (savingClient) return;
    setSavingClient(true);
    try {
      const payload = {
        ...newClient,
        contacts: newClient.contacts.filter((c) => c.name || c.email),
        opportunity_value: newClient.opportunity_value ? parseFloat(newClient.opportunity_value) : null,
        expected_close_date: newClient.expected_close_date || null,
      };

      const response = await fetch(`${API}/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const created = await response.json();
        setClients((prev) => [created, ...prev]);
        setShowAddModal(false);
        setNewClient({
          company_name: "",
          tier: "",
          sector: "",
          city: "",
          stage: "Unqualified Leads",
          engagement_type: "Commercial Opportunity",
          opportunity_value: "",
          opportunity_currency: "NGN",
          expected_close_date: "",
          contacts: [{ name: "", email: "", phone: "", title: "" }],
          tracking_notes: "",
        });
        toast.success("Client created successfully");
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err?.detail || "Failed to create client");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error("Failed to create client");
    } finally {
      setSavingClient(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setClients((prev) => prev.filter((c) => c.client_id !== clientId));
        toast.success("Client deleted");
      } else {
        toast.error("Failed to delete client");
      }
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const handleUpdateClient = async (clientId, field, value) => {
    try {
      const response = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [field]: value }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClients((prev) =>
          prev.map((c) =>
            c.client_id === clientId ? updated : c
          )
        );
        toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`);
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail || `Failed to update ${field}`);
      }
    } catch (error) {
      toast.error(`Failed to update ${field}`);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API}/clients/bulk-upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Successfully imported ${result.clients_created} clients`);
        if (result.errors?.length > 0) {
          toast.warning(`${result.errors.length} rows had errors`);
        }
        fetchClients();
        setShowUploadModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload file");
      }
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/clients/export-excel`, {
        credentials: "include",
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const filename = disposition?.match(/filename="(.+)"/)?.[1] || "rex_clients.xlsx";
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Clients exported successfully");
      } else {
        toast.error("Failed to export clients");
      }
    } catch (error) {
      toast.error("Failed to export clients");
    } finally {
      setExporting(false);
    }
  };

  const handleReplaceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.confirm(
      "This will replace your entire client list with the contents of this file. " +
      "Clients not in the file will be deleted. Are you sure?"
    )) {
      if (fileReplaceInputRef.current) fileReplaceInputRef.current.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API}/clients/import-replace`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Imported ${result.clients_imported} clients, removed ${result.clients_deleted} old clients`
        );
        if (result.errors?.length > 0) {
          toast.warning(`${result.errors.length} rows had errors`);
        }
        fetchClients();
        setShowReplaceModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to replace clients");
      }
    } catch (error) {
      toast.error("Failed to replace clients");
    } finally {
      setUploading(false);
      if (fileReplaceInputRef.current) {
        fileReplaceInputRef.current.value = "";
      }
    }
  };

  const filteredClients = clients.filter((client) => {
    // Search across multiple fields
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      client.company_name?.toLowerCase().includes(searchLower) ||
      client.sector?.toLowerCase().includes(searchLower) ||
      client.city?.toLowerCase().includes(searchLower) ||
      client.country?.toLowerCase().includes(searchLower) ||
      client.account_manager_name?.toLowerCase().includes(searchLower) ||
      client.deal_owner_name?.toLowerCase().includes(searchLower) ||
      client.contacts?.some(c => 
        c.name?.toLowerCase().includes(searchLower) ||
        c.email?.toLowerCase().includes(searchLower)
      );

    const matchesStage = filterStage === "all" || client.stage === filterStage;
    const matchesTier = filterTier === "all" || client.tier === filterTier;
    const matchesSector = filterSector === "all" || client.sector === filterSector;
    const matchesStatus = filterStatus === "all" || client.status === filterStatus;
    const matchesCountry = filterCountry === "all" || client.country === filterCountry;

    return matchesSearch && matchesStage && matchesTier && matchesSector && matchesStatus && matchesCountry;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterStage("all");
    setFilterTier("all");
    setFilterSector("all");
    setFilterStatus("all");
    setFilterCountry("all");
  };

  const hasActiveFilters = searchQuery || filterStage !== "all" || filterTier !== "all" || 
    filterSector !== "all" || filterStatus !== "all" || filterCountry !== "all";

  const getStageColor = (stage) => {
    const found = STAGES.find((s) => s.id === stage);
    return found ? found.color : "bg-slate-500";
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return "-";
    }
  };

  // Format currency value
  const formatCurrency = (value, currency) => {
    if (!value) return "-";
    const curr = currency || "NGN";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency: curr, maximumFractionDigits: 0 }).format(value);
  };

  // Render cell content based on column type
  const renderCellContent = (client, columnId) => {
    switch (columnId) {
      case "health_score":
        return <TrafficLight healthScore={client.health_score} />;
      
      case "company_name":
        return (
          <Link
            to={`/clients/${client.client_id}`}
            className="font-medium text-slate-900 hover:text-blue-600"
          >
            {client.company_name}
          </Link>
        );
      
      case "stage":
        if (!canEdit) {
          return (
            <Badge className={`${getStageColor(client.stage)} text-white text-xs`}>
              {client.stage || "-"}
            </Badge>
          );
        }
        return (
          <Select
            value={client.stage || ""}
            onValueChange={(value) => handleUpdateClient(client.client_id, "stage", value)}
          >
            <SelectTrigger className="h-7 w-[130px] border-0 bg-transparent hover:bg-slate-100 p-1">
              <Badge className={`${getStageColor(client.stage)} text-white text-xs cursor-pointer`}>
                {client.stage || "Set Stage"}
              </Badge>
            </SelectTrigger>
            <SelectContent>
              {STAGES.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>
                    {stage.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "status":
        if (!canEdit) {
          return client.status ? (
            <Badge variant="outline" className="text-xs">{client.status}</Badge>
          ) : <span className="text-slate-400 text-xs">-</span>;
        }
        return (
          <Select
            value={client.status || ""}
            onValueChange={(value) => handleUpdateClient(client.client_id, "status", value)}
          >
            <SelectTrigger className="h-7 w-[100px] border-0 bg-transparent hover:bg-slate-100 p-1">
              {client.status ? (
                <Badge variant="outline" className="text-xs cursor-pointer">
                  {client.status}
                </Badge>
              ) : (
                <span className="text-xs text-slate-400">Set Status</span>
              )}
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "tier":
        if (!canEdit) {
          return <span className="text-slate-600">{client.tier || "-"}</span>;
        }
        return (
          <Select
            value={client.tier || ""}
            onValueChange={(value) => handleUpdateClient(client.client_id, "tier", value)}
          >
            <SelectTrigger className="h-7 w-[80px] border-0 bg-transparent hover:bg-slate-100 p-1 text-xs">
              <span className="text-slate-600">{client.tier || "Set Tier"}</span>
            </SelectTrigger>
            <SelectContent>
              {TIERS.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "confidence_level":
        if (!canEdit) {
          const confColors2 = { High: "bg-emerald-100 text-emerald-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-red-100 text-red-700" };
          return client.confidence_level ? (
            <Badge className={confColors2[client.confidence_level] || "bg-slate-100"}>{client.confidence_level}</Badge>
          ) : <span className="text-slate-400 text-xs">-</span>;
        }
        return (
          <Select
            value={client.confidence_level || ""}
            onValueChange={(v) => handleUpdateClient(client.client_id, "confidence_level", v)}
          >
            <SelectTrigger className="h-7 w-[90px] border-0 bg-transparent hover:bg-slate-100 p-1 text-xs">
              {(() => {
                const confColors3 = { High: "bg-emerald-100 text-emerald-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-red-100 text-red-700" };
                return client.confidence_level ? (
                  <Badge className={confColors3[client.confidence_level] || "bg-slate-100"}>{client.confidence_level}</Badge>
                ) : <span className="text-slate-400 text-xs">Set</span>;
              })()}
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCE_LEVELS.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
        );
      
      case "opportunity_value":
        if (!canEdit) return <span className="text-slate-600">{formatCurrency(client.opportunity_value, client.currency)}</span>;
        return (
          <InlineEdit
            value={client.opportunity_value}
            type="number"
            placeholder="Set value"
            onSave={(v) => handleUpdateClient(client.client_id, "opportunity_value", v)}
          />
        );
      
      case "expected_close_date":
        if (!canEdit) return <span className="text-slate-600 text-sm">{formatDate(client.expected_close_date)}</span>;
        return (
          <InlineEdit
            value={client.expected_close_date ? client.expected_close_date.split("T")[0] : ""}
            type="date"
            placeholder="Set date"
            onSave={(v) => handleUpdateClient(client.client_id, "expected_close_date", v)}
          />
        );

      case "engagement_type":
        if (!canEdit) return <span className="text-slate-600 text-xs">{client.engagement_type || "-"}</span>;
        return (
          <Select
            value={client.engagement_type || ""}
            onValueChange={(v) => handleUpdateClient(client.client_id, "engagement_type", v)}
          >
            <SelectTrigger className="h-7 w-[130px] border-0 bg-transparent hover:bg-slate-100 p-1 text-xs">
              <span className="text-slate-600 truncate">{client.engagement_type || "Set type"}</span>
            </SelectTrigger>
            <SelectContent>
              {ENGAGEMENT_TYPES.map((t) => (<SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>))}
            </SelectContent>
          </Select>
        );
      
      case "last_contacted_date":
        return <span className="text-slate-600 text-sm">{formatDate(client.last_contacted_date)}</span>;
      
      case "next_action_date":
        return <span className="text-slate-600 text-sm">{client.next_action_date ? formatDate(client.next_action_date) : client.next_action || "-"}</span>;
      
      case "sector":
        return <span className="text-slate-600 max-w-[200px] truncate block" title={client.sector}>{client.sector || "-"}</span>;
      
      case "country":
        return <span className="text-slate-600">{client.country || "-"}</span>;
      
      case "city":
        return <span className="text-slate-600">{client.city || "-"}</span>;
      
      case "account_manager_name":
        if (!canEdit) {
          return <span className="text-slate-600">{client.status === "Dormant" ? "N/A" : (client.account_manager_name || "-")}</span>;
        }
        if (client.status === "Dormant") {
          return <span className="text-slate-400 italic">N/A</span>;
        }
        return (
          <Select
            value={client.account_manager_id || ""}
            onValueChange={async (value) => {
              const selectedUser = users.find(u => u.user_id === value);
              if (selectedUser) {
                try {
                  const response = await fetch(`${API}/clients/${client.client_id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                      account_manager_id: selectedUser.user_id,
                      account_manager_name: selectedUser.name,
                    }),
                  });
                  if (response.ok) {
                    setClients((prev) =>
                      prev.map((c) =>
                        c.client_id === client.client_id
                          ? { ...c, account_manager_id: selectedUser.user_id, account_manager_name: selectedUser.name }
                          : c
                      )
                    );
                    toast.success(`Account Manager changed to ${selectedUser.name}`);
                  } else {
                    toast.error("Failed to change Account Manager");
                  }
                } catch (error) {
                  toast.error("Failed to change Account Manager");
                }
              }
            }}
          >
            <SelectTrigger className="h-7 w-[120px] border-0 bg-transparent hover:bg-slate-100 p-1 text-xs">
              <span className="text-slate-600 truncate">{client.account_manager_name || "Assign AM"}</span>
            </SelectTrigger>
            <SelectContent>
              {users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "deal_owner_name":
        if (!canEdit) {
          return <span className="text-slate-600">{client.deal_owner_name || "-"}</span>;
        }
        return (
          <Select
            value={client.deal_owner_id || ""}
            onValueChange={async (value) => {
              const selectedUser = users.find(u => u.user_id === value);
              if (selectedUser) {
                try {
                  const response = await fetch(`${API}/clients/${client.client_id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                      deal_owner_id: selectedUser.user_id,
                      deal_owner_name: selectedUser.name,
                    }),
                  });
                  if (response.ok) {
                    setClients((prev) =>
                      prev.map((c) =>
                        c.client_id === client.client_id
                          ? { ...c, deal_owner_id: selectedUser.user_id, deal_owner_name: selectedUser.name }
                          : c
                      )
                    );
                    toast.success(`Deal Owner changed to ${selectedUser.name}`);
                  } else {
                    toast.error("Failed to change Deal Owner");
                  }
                } catch (error) {
                  toast.error("Failed to change Deal Owner");
                }
              }
            }}
          >
            <SelectTrigger className="h-7 w-[120px] border-0 bg-transparent hover:bg-slate-100 p-1 text-xs">
              <span className="text-slate-600 truncate">{client.deal_owner_name || "Assign DO"}</span>
            </SelectTrigger>
            <SelectContent>
              {users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "currency":
        return <span className="text-slate-600">{client.currency || "-"}</span>;
      
      case "next_step":
        if (!canEdit) return <span className="text-slate-600 text-sm max-w-[150px] truncate block" title={client.next_step}>{client.next_step || "-"}</span>;
        return (
          <InlineEdit
            value={client.next_step}
            placeholder="Set next step"
            onSave={(v) => handleUpdateClient(client.client_id, "next_step", v)}
            className="text-sm max-w-[150px] truncate block"
          />
        );
      
      case "potential_offering":
        if (!canEdit) return <span className="text-slate-600 text-sm max-w-[150px] truncate block" title={client.potential_offering}>{client.potential_offering || "-"}</span>;
        return (
          <InlineEdit
            value={client.potential_offering}
            placeholder="Set offering"
            onSave={(v) => handleUpdateClient(client.client_id, "potential_offering", v)}
            className="text-sm max-w-[150px] truncate block"
          />
        );
      
      case "tracking_notes":
        if (!canEdit) return <span className="text-slate-600 text-sm max-w-[200px] truncate block" title={client.tracking_notes}>{client.tracking_notes || "-"}</span>;
        return (
          <InlineEdit
            value={client.tracking_notes}
            placeholder="Add notes"
            onSave={(v) => handleUpdateClient(client.client_id, "tracking_notes", v)}
            className="text-sm max-w-[200px] truncate block"
          />
        );
      
      case "company_type":
        return <span className="text-slate-600">{client.company_type || "-"}</span>;
      
      default:
        return <span className="text-slate-600">{client[columnId] || "-"}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope']">
            Clients
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {clients.length} total clients
            {!canEdit && <span className="ml-2 text-amber-600">(Read-only)</span>}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {isAdmin && <PipelineRoundTrip />}
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              size="sm"
              onClick={() => setShowAddModal(true)}
              data-testid="add-client-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Client
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-slate-700 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </h3>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                <X className="w-4 h-4 mr-1" /> Clear Filters
              </Button>
            )}
            <Sheet open={showColumnSettings} onOpenChange={setShowColumnSettings}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" data-testid="column-settings-btn">
                  <Settings2 className="w-4 h-4 mr-1" /> Columns
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Customize Columns</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  <p className="text-sm text-slate-500">
                    Select columns to display and drag to reorder them.
                  </p>
                  <div className="space-y-2">
                    {ALL_COLUMNS.map((column) => (
                      <div
                        key={column.id}
                        draggable={visibleColumns.includes(column.id)}
                        onDragStart={(e) => handleDragStart(e, column.id)}
                        onDragOver={(e) => handleDragOver(e, column.id)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 p-2 rounded-lg border ${
                          draggedColumn === column.id ? "opacity-50 bg-blue-50 border-blue-300" : 
                          visibleColumns.includes(column.id) ? "bg-slate-50 border-slate-200" : "bg-white border-slate-100"
                        } ${visibleColumns.includes(column.id) ? "cursor-grab" : ""}`}
                      >
                        {visibleColumns.includes(column.id) && (
                          <GripVertical className="w-4 h-4 text-slate-400" />
                        )}
                        <Checkbox
                          id={`col-${column.id}`}
                          checked={visibleColumns.includes(column.id)}
                          onCheckedChange={() => toggleColumn(column.id)}
                        />
                        <label 
                          htmlFor={`col-${column.id}`}
                          className="flex-1 text-sm cursor-pointer"
                        >
                          {column.label}
                        </label>
                        {column.id === "health_score" && (
                          <div className="flex gap-1">
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                            <div className="w-2 h-2 rounded-full bg-red-500" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setVisibleColumns(DEFAULT_COLUMNS);
                        setColumnOrder(DEFAULT_COLUMNS);
                        savePreferences(DEFAULT_COLUMNS, DEFAULT_COLUMNS);
                        toast.success("Columns reset to default");
                      }}
                    >
                      Reset to Default
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search company, contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-sm"
              data-testid="search-clients-input"
            />
          </div>
          
          {/* Sector Filter - Dynamic from data */}
          <Select value={filterSector} onValueChange={setFilterSector}>
            <SelectTrigger className="w-[150px] sm:w-[180px] text-xs sm:text-sm" data-testid="filter-sector-select">
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors ({clients.length})</SelectItem>
              {uniqueSectors.map((sector) => {
                const count = clients.filter(c => c.sector?.includes(sector)).length;
                return (
                  <SelectItem key={sector} value={sector}>
                    {sector.length > 25 ? sector.substring(0, 25) + '...' : sector} ({count})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Stage Filter */}
          <Select value={filterStage} onValueChange={setFilterStage}>
            <SelectTrigger className="w-[160px]" data-testid="filter-stage-select">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {STAGES.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>
                  {stage.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Tier Filter */}
          <Select value={filterTier} onValueChange={setFilterTier}>
            <SelectTrigger className="w-[130px]" data-testid="filter-tier-select">
              <SelectValue placeholder="All Tiers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              {TIERS.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter - Dynamic from data */}
          {uniqueStatuses.length > 0 && (
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status-select">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {uniqueStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Country Filter - Dynamic from data */}
          {uniqueCountries.length > 1 && (
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger className="w-[140px]" data-testid="filter-country-select">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {uniqueCountries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        
        {/* Results count */}
        <div className="text-sm text-slate-500">
          Showing <span className="font-semibold text-slate-700">{filteredClients.length}</span> of {clients.length} clients
          {hasActiveFilters && <span className="text-blue-600"> (filtered)</span>}
        </div>
      </div>

      {/* Clients Table */}
      <div className="border rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {getOrderedColumns().map((column) => (
                <TableHead key={column.id} className={column.width}>
                  {column.label}
                </TableHead>
              ))}
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={getOrderedColumns().length + 1} className="text-center py-8 text-slate-500">
                  {clients.length === 0
                    ? "No clients yet. Add your first client or upload from Excel."
                    : "No clients match your filters."}
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.client_id} data-testid={`client-row-${client.client_id}`}>
                  {getOrderedColumns().map((column) => (
                    <TableCell key={column.id}>
                      {renderCellContent(client, column.id)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/clients/${client.client_id}`}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/clients/${client.client_id}?tab=email`}>
                            <Mail className="w-4 h-4 mr-2" /> Send Email
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteClient(client.client_id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={newClient.company_name}
                onChange={(e) =>
                  setNewClient({ ...newClient, company_name: e.target.value })
                }
                placeholder="Enter company name"
                data-testid="new-client-company-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="engagement_type">Engagement Type</Label>
                <Select
                  value={newClient.engagement_type}
                  onValueChange={(v) => setNewClient({ ...newClient, engagement_type: v })}
                >
                  <SelectTrigger data-testid="new-client-engagement-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENGAGEMENT_TYPES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="stage">Stage</Label>
                <Select
                  value={newClient.stage}
                  onValueChange={(v) => setNewClient({ ...newClient, stage: v })}
                >
                  <SelectTrigger data-testid="new-client-stage">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tier">Tier</Label>
                <Select
                  value={newClient.tier}
                  onValueChange={(v) => setNewClient({ ...newClient, tier: v })}
                >
                  <SelectTrigger data-testid="new-client-tier">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIERS.map((tier) => (
                      <SelectItem key={tier} value={tier}>
                        {tier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sector">Sector</Label>
                <Select
                  value={newClient.sector}
                  onValueChange={(v) => setNewClient({ ...newClient, sector: v })}
                >
                  <SelectTrigger data-testid="new-client-sector">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTORS.map((sector) => (
                      <SelectItem key={sector} value={sector}>
                        {sector}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newClient.city}
                  onChange={(e) =>
                    setNewClient({ ...newClient, city: e.target.value })
                  }
                  placeholder="Lagos, Abuja, etc."
                />
              </div>
              <div>
                <Label>Opportunity Value</Label>
                <div className="flex gap-2">
                  <Select
                    value={newClient.opportunity_currency}
                    onValueChange={(v) => setNewClient({ ...newClient, opportunity_currency: v })}
                  >
                    <SelectTrigger className="w-[90px]" data-testid="new-client-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={newClient.opportunity_value}
                    onChange={(e) => setNewClient({ ...newClient, opportunity_value: e.target.value })}
                    placeholder="0"
                    data-testid="new-client-opportunity-value"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Expected Close Date</Label>
              <Input
                type="date"
                value={newClient.expected_close_date}
                onChange={(e) => setNewClient({ ...newClient, expected_close_date: e.target.value })}
                data-testid="new-client-expected-close-date"
              />
            </div>
            <div>
              <Label>Primary Contact</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  placeholder="Contact name"
                  value={newClient.contacts[0].name}
                  onChange={(e) =>
                    setNewClient({
                      ...newClient,
                      contacts: [{ ...newClient.contacts[0], name: e.target.value }],
                    })
                  }
                />
                <Input
                  placeholder="Email"
                  type="email"
                  value={newClient.contacts[0].email}
                  onChange={(e) =>
                    setNewClient({
                      ...newClient,
                      contacts: [{ ...newClient.contacts[0], email: e.target.value }],
                    })
                  }
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newClient.tracking_notes}
                onChange={(e) =>
                  setNewClient({ ...newClient, tracking_notes: e.target.value })
                }
                placeholder="Any initial notes about this client..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={savingClient}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreateClient}
              disabled={savingClient}
              data-testid="create-client-btn"
            >
              {savingClient ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" data-testid="create-client-spinner" />Saving client…</>) : "Create Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload Clients</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Upload an Excel (.xlsx) or CSV file with your client data. The file
              should include columns like: Company Name, Tier, Sector, City, Status,
              etc.
            </p>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 mb-2">
                Drag and drop your file here, or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outline"
                  className="mt-2"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    {uploading ? "Uploading..." : "Select File"}
                  </span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Supported formats: .xlsx, .xls, .csv
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace from Excel Modal */}
      <Dialog open={showReplaceModal} onOpenChange={setShowReplaceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace Client List from Excel</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800 font-medium">How this works:</p>
              <ol className="text-sm text-amber-700 mt-1 list-decimal list-inside space-y-1">
                <li>First <strong>Export Excel</strong> to download your current clients</li>
                <li>Edit the file — delete rows you don't want</li>
                <li>Upload the edited file here to replace your client list</li>
              </ol>
              <p className="text-xs text-amber-600 mt-2">
                Clients not in the uploaded file will be permanently deleted.
              </p>
            </div>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <RefreshCw className="w-10 h-10 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 mb-2">
                Upload your edited Excel file to replace the client list
              </p>
              <input
                ref={fileReplaceInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleReplaceUpload}
                className="hidden"
                id="file-replace-upload"
                data-testid="replace-file-input"
              />
              <label htmlFor="file-replace-upload">
                <Button
                  variant="outline"
                  className="mt-2"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    {uploading ? "Replacing..." : "Select File"}
                  </span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Supported formats: .xlsx, .xls, .csv
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplaceModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
